import React, { useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import MainLayout from "../../../../layouts/MainLayout";
import "../../../../assets/styles/custom-styles.css";

import Form from "../../../../components/forms/Form";
import SimpleBody from "../../../../components/forms/SimpleBody";
import SectionHeader from "../../../../components/forms/SectionHeader";
import PageHeader from "../../../../components/PageHeader";

import { eventItemFields } from "../../../../constants/forms/eventItemFields";
import { entities } from "../../../../constants/entities";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";

import useLoader from "../../../../hooks/useLoader";
import useForm from "../../../../hooks/useForm";
import useBaseService from "../../../../hooks/services/useBaseService";
import useNotification from "../../../../hooks/useNotification";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const EditEventItemContractPage = () => {
  const navigate = useNavigate();
  const { contractId, eventId, additiveId, itemId } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, formatData, handleChange, formErrors, resetForm } = useForm(
    setDefaultFieldValues(eventItemFields),
  );

  // Query para buscar os dados do item do evento
  const {
    data: eventItemData,
    isLoading,
    isError,
    error,
  } = useQueryWithLoader({
    queryKey: ["eventItem", contractId, eventId, additiveId, itemId],
    queryFn: async () => {
      const response = await baseServiceGetByColumn(
        entities.contracts.eventAdditiveItemUpdate(
          contractId,
          eventId,
          additiveId,
          itemId,
        ),
      );
      return response.result;
    },
    enabled: !!contractId && !!eventId && !!additiveId && !!itemId,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  // Efeito para preencher o formulário quando os dados da query estiverem disponíveis
  useEffect(() => {
    if (!isLoading && !isError && eventItemData) {
      formatData(eventItemData, eventItemFields);
    }
    if (isError && error) {
      showNotification(
        "error",
        `Erro ao carregar item do evento: ${
          error.message || error.response?.data?.message || "Erro desconhecido."
        }`,
      );
      navigate(`/aluguel/contratos/${contractId}/eventos/${eventId}/detalhes/`);
    }
  }, [
    isLoading,
    isError,
    error,
    eventItemData,
    formatData,
    navigate,
    contractId,
    eventId,
  ]);

  // Mutation para a edição do item do evento
  const updateEventItemMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      return await baseServicePut(
        entities.contracts.eventAdditiveItemUpdate(
          contractId,
          eventId,
          additiveId,
          itemId,
        ),
        dataToUpdate,
      );
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        resetForm();
        queryClient.invalidateQueries({
          queryKey: ["eventItem", contractId, eventId, additiveId, itemId],
        });
        queryClient.invalidateQueries({
          queryKey: ["eventItems", contractId, eventId],
        });
        queryClient.invalidateQueries({ queryKey: ["contractEventItems"] });
        navigate(
          `/aluguel/contratos/${contractId}/eventos/${eventId}/detalhes/`,
        );
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
  });

  // Controla o loader global baseado nos estados de carregamento do React Query
  useEffect(() => {
    if (isLoading || updateEventItemMutation.isPending) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoading, updateEventItemMutation.isPending, showLoader, hideLoader]);

  // Handler para submeter o formulário
  const handleSubmit = useCallback(() => {
    try {
      updateEventItemMutation.mutate(formData);
    } catch (error) {
      console.error("Erro ao enviar item do evento:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateEventItemMutation]);

  // Handler para o botão "Voltar"
  const handleBack = useCallback(() => {
    navigate(`/aluguel/contratos/${contractId}/eventos/${eventId}/detalhes`);
  }, [navigate, contractId, eventId]);

  // Exibir mensagem de erro ou carregamento inicial
  if (isError && !isLoading && !eventItemData) {
    return (
      <MainLayout selectedCompany="ALUCOM">
        <PageHeader
          title="Erro ao Carregar Item"
          showBackButton={true}
          backUrl={`/aluguel/contratos/${contractId}/eventos/${eventId}/detalhes/`}
        />
        <div className="container-fluid p-3 text-center">
          <p>
            Não foi possível carregar os detalhes do item do evento.{" "}
            {error?.message}
          </p>
          <button
            className="btn btn-primary"
            onClick={() =>
              queryClient.invalidateQueries([
                "eventItem",
                contractId,
                eventId,
                additiveId,
                itemId,
              ])
            }
          >
            Tentar Novamente
          </button>
        </div>
      </MainLayout>
    );
  }

  // Renderiza o formulário somente após o carregamento inicial dos dados
  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Editar Item do Evento"
        showBackButton={true}
        backUrl={`/aluguel/contratos/${contractId}/eventos/${eventId}/detalhes/`}
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Editar"
          textLoadingSubmit="Editando..."
          handleBack={handleBack}
          isLoading={updateEventItemMutation.isPending}
        >
          {() => {
            const renderedSections = new Set();
            return eventItemFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={() => []} // Ajuste conforme a necessidade de obter opções para selects
                    getSelectedValue={(fieldId, options) => {
                      const value = formData[fieldId];
                      if (fieldId.includes(".")) {
                        const [parentKey, childKey] = fieldId.split(".");
                        const nestedValue = formData[parentKey]?.[childKey];
                        return (
                          options.find(
                            (option) => option.value === nestedValue,
                          ) || null
                        );
                      }
                      return (
                        options.find((option) => option.value === value) || null
                      );
                    }}
                    readOnly={isLoading || updateEventItemMutation.isPending}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditEventItemContractPage;
